"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import MealCard from "@/components/MealCard";
import { useApp } from "@/contexts/AppContext";
import {
  FaUtensils,
  FaExclamationTriangle,
  FaMapMarkerAlt,
} from "react-icons/fa";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  doc,
  getDoc,
  startAfter,
  DocumentSnapshot,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { trackBrowseMealsStarted } from "@/lib/analytics";
import { Meal } from "@/types/meal";
import { calculateDistance, getUserLocation } from "@/utils/distance";
import { SEARCH_CONFIG } from "@/config/search";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import algoliasearch from "algoliasearch/lite";
import {
  InstantSearch,
  useSearchBox,
  useInfiniteHits,
  Configure,
} from "react-instantsearch";
import {
  Container,
  Content,
  PageTitle,
  Subtitle,
  FiltersBar,
  LocationNotice,
  ZipCodePrompt,
  ZipCodeTitle,
  ZipCodeText,
  ZipCodeForm,
  ZipCodeInput,
  ZipCodeButton,
  ErrorText,
  SearchContainer,
  SearchBox,
  SearchIcon,
  SearchInput,
  ClearButton,
  FilterButton,
  MealsGrid,
  LoadingContainer,
  Spinner,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
  LoadMoreContainer,
  LoadMoreSpinner,
  LoadMoreText,
  LocationGateOverlay,
  LocationGateModal,
  LocationGateIcon,
  LocationGateTitle,
  LocationGateText,
} from "./page.styles";

const searchClient = algoliasearch(
  "A3SZLBMAVM",
  "4411acf58100ace44aa231494b92fb2d",
);

// Algolia Search Components
function SearchBoxComponent() {
  const { query, refine } = useSearchBox();
  const [inputValue, setInputValue] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      refine(inputValue);
    }, 300); // Debounce search
    return () => clearTimeout(timer);
  }, [inputValue, refine]);

  // Sync inputValue with query when query changes externally
  useEffect(() => {
    setInputValue(query);
  }, [query]);

  return (
    <SearchContainer>
      <SearchBox>
        <SearchIcon>🔍</SearchIcon>
        <SearchInput
          type="text"
          placeholder="Search for meals, artisans, or categories..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        {inputValue && (
          <ClearButton
            onClick={() => setInputValue("")}
            aria-label="Clear search"
          >
            ✕
          </ClearButton>
        )}
      </SearchBox>
    </SearchContainer>
  );
}

interface SearchResultsProps {
  userLocation: { lat: number; lng: number } | null;
  onMealClick: (mealId: string) => void;
  onArtisanClick: (uid: string) => void;
  isSearching: boolean;
}

function SearchResultsComponent({
  userLocation,
  onMealClick,
  onArtisanClick,
  isSearching,
}: SearchResultsProps) {
  const { hits, isLastPage, showMore } = useInfiniteHits();
  const { query } = useSearchBox();

  // Only show search results if there's a query
  if (!query || query.trim() === "") {
    return null; // Return null to show regular meals instead
  }

  if (isSearching) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (hits.length === 0) {
    return (
      <EmptyState>
        <EmptyIcon>🔍</EmptyIcon>
        <EmptyTitle>No results found</EmptyTitle>
        <EmptyText>
          No meals match your search &quot;{query}&quot;. Try different keywords
          or clear the search to see all meals.
        </EmptyText>
      </EmptyState>
    );
  }

  // Convert Algolia hits to Meal objects
  const meals: Meal[] = hits.map((hit: any) => {
    let distance: number | undefined = undefined;
    if (hit._geoloc && userLocation) {
      distance = calculateDistance(
        userLocation.lat,
        userLocation.lng,
        hit._geoloc.lat,
        hit._geoloc.lng,
      );
    }

    return {
      id: hit.objectID,
      uid: hit.uid || "",
      title: hit.title || "",
      description: hit.description || "",
      price: hit.price || 0,
      image: hit.image || "",
      producer: hit.producer || "",
      rating: hit.rating || 0,
      ratingCount: hit.ratingCount || 0,
      available: hit.available || 0,
      setFoodCategories: hit.setFoodCategories || [],
      mealType: hit.mealType,
      distance,
      mealActive: hit.mealActive,
      deliver: hit.deliver,
      pickup: hit.pickup,
      lat: hit._geoloc?.lat,
      lng: hit._geoloc?.lng,
    };
  });

  return (
    <>
      <MealsGrid>
        {meals.map((meal) => (
          <MealCard
            key={meal.id}
            meal={meal}
            onClick={() => onMealClick(meal.id)}
            onArtisanClick={() => onArtisanClick(meal.uid)}
          />
        ))}
      </MealsGrid>
      {!isLastPage && (
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <button
            onClick={showMore}
            style={{
              padding: "0.75rem 2rem",
              background: "#c8553d",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Load More
          </button>
        </div>
      )}
    </>
  );
}

export default function FeedPage() {
  const router = useRouter();
  const { user } = useApp();
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [filter, setFilter] = useState<"all" | "localOnly">("all");
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [locationError, setLocationError] = useState<string>("");
  const [isArtisan, setIsArtisan] = useState(false);
  const [zipCode, setZipCode] = useState<string>("");
  const [zipCodeLoading, setZipCodeLoading] = useState(false);
  const [zipCodeError, setZipCodeError] = useState<string>("");
  const [showLocationGate, setShowLocationGate] = useState(false);

  // Ref for infinite scroll observer
  const observerTarget = useRef<HTMLDivElement>(null);

  const MEALS_PER_PAGE = 12;

  useEffect(() => {
    void trackBrowseMealsStarted();
  }, []);

  // Check if user is an artisan
  useEffect(() => {
    const checkArtisanStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await getDoc(doc(db, "accounts", user.uid));
          if (userDoc.exists()) {
            setIsArtisan(userDoc.data().isArtisan || false);
          }
        } catch (error) {
          console.error("Error checking artisan status:", error);
        }
      }
    };

    checkArtisanStatus();
  }, [user]);

  // Get user location from browser
  useEffect(() => {
    // First, check if we have a stored location from zip code
    const storedLocation = sessionStorage.getItem("userLocation");
    const storedZipCode = sessionStorage.getItem("userZipCode");

    if (storedLocation && storedZipCode) {
      try {
        const location = JSON.parse(storedLocation);
        setUserLocation(location);
        setZipCode(storedZipCode);
        setLocationError("");
        setLoading(false);
        return;
      } catch (error) {
        console.error("Error parsing stored location:", error);
        sessionStorage.removeItem("userLocation");
        sessionStorage.removeItem("userZipCode");
      }
    }

    // Always try to get browser location (even if previously denied)
    // This allows users to change their permission and have it work immediately
    getUserLocation()
      .then((location) => {
        setUserLocation(location);
        setLocationError("");
        setLoading(false); // Stop loading when we have location (loadMeals will set it back to true)
        // Clear the denied flag if location is now allowed
        sessionStorage.removeItem("locationDenied");
      })
      .catch((error) => {
        console.error("Location error:", error);

        // Store that location was denied so we remember on reload
        sessionStorage.setItem("locationDenied", "true");

        // Set all states together
        setUserLocation(null); // Explicitly set to null first
        setLocationError(
          "Share your location with us to discover amazing artisans and delicious homemade meals near you! Enable location access in your browser or enter your zip code below.",
        );
        setLoading(false); // Stop loading when location is denied

        // Show location gate popup immediately when user lands without location
        setShowLocationGate(true);
      });
  }, []);

  // Handle zip code submission
  const handleZipCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!zipCode.trim()) {
      setZipCodeError("Please enter a zip code");
      return;
    }

    // Basic US zip code validation (5 digits or 5+4 format)
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(zipCode.trim())) {
      setZipCodeError("Please enter a valid US zip code");
      return;
    }

    setZipCodeLoading(true);
    setZipCodeError("");

    try {
      // Call Google Maps Geocoding API
      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        throw new Error("Google Maps API key not configured");
      }

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          zipCode.trim(),
        )}&key=${apiKey}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch location data");
      }

      const data = await response.json();

      if (data.status === "OK" && data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        const coords = {
          lat: location.lat,
          lng: location.lng,
        };

        setUserLocation(coords);
        setLocationError("");
        // Store in session storage for persistence
        sessionStorage.setItem("userLocation", JSON.stringify(coords));
        sessionStorage.setItem("userZipCode", zipCode.trim());
        // Clear the location denied flag since user provided zip code
        sessionStorage.removeItem("locationDenied");
        // Close the location gate overlay
        setShowLocationGate(false);
      } else {
        setZipCodeError("Could not find location for this zip code");
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      setZipCodeError("Failed to get location. Please try again.");
    } finally {
      setZipCodeLoading(false);
    }
  };

  // Load meals
  useEffect(() => {
    loadMeals();
  }, [filter, userLocation]);

  // Infinite scroll: Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // If the target element is visible and we're not already loading
        if (entries[0].isIntersecting && !loadingMore && hasMore && !loading) {
          loadMeals(true);
        }
      },
      { threshold: 0.1, rootMargin: "100px" }, // Trigger when 10% visible or 100px before
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loadingMore, hasMore, loading, lastDoc, filter, userLocation]);

  const loadMeals = async (loadMore = false) => {
    // Don't load more if we're already loading or there's no more data
    if (loadMore && (loadingMore || !hasMore)) {
      return;
    }

    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setMeals([]); // Clear meals on initial load
      setLastDoc(null); // Reset pagination
      setHasMore(true);
    }

    try {
      const mealsRef = collection(db, "meals");

      // Simple query - fetch meals and sort them based on whether we have location
      const mealsQuery =
        loadMore && lastDoc
          ? query(
              mealsRef,
              where("available", "==", 0),
              orderBy(userLocation ? "createdAt" : "rating", "desc"),
              startAfter(lastDoc),
              limit(MEALS_PER_PAGE),
            )
          : query(
              mealsRef,
              where("available", "==", 0),
              orderBy(userLocation ? "createdAt" : "rating", "desc"),
              limit(MEALS_PER_PAGE),
            );

      const snapshot = await getDocs(mealsQuery);

      // Check if there are more results
      if (snapshot.size < MEALS_PER_PAGE) {
        setHasMore(false);
      }

      // Save last document for pagination
      if (snapshot.docs.length > 0) {
        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      }

      const mealsList: Meal[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();

        // Calculate distance if location is available
        let distance: number | undefined = undefined;
        if (data.lat && data.lng && userLocation) {
          // Ensure lat/lng are numbers (they might be stored as strings)
          const mealLat =
            typeof data.lat === "string" ? parseFloat(data.lat) : data.lat;
          const mealLng =
            typeof data.lng === "string" ? parseFloat(data.lng) : data.lng;

          // Only calculate if we have valid numbers
          if (!isNaN(mealLat) && !isNaN(mealLng)) {
            distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              mealLat,
              mealLng,
            );
          }
        }

        // Phase 2 update: Apply distance filter only for "Local Meals Only"
        const shouldIncludeMeal =
          filter === "localOnly"
            ? distance !== undefined &&
              distance <= SEARCH_CONFIG.DEFAULT_RADIUS_MILES
            : true; // "All Meals" includes everything

        if (shouldIncludeMeal) {
          mealsList.push({
            id: doc.id,
            uid: data.uid || "",
            title: data.title || "",
            description: data.description || "",
            price: data.price || 0,
            image: data.imageUrl || data.image || "",
            producer: data.chefName || data.producer || "",
            rating: data.rating || 0,
            ratingCount: data.ratingCount || 0,
            available: data.available || 0,
            setFoodCategories: data.setFoodCategories || [],
            mealType: data.mealType,
            distance,
            mealActive: data.mealActive,
            deliver: data.deliver,
            pickup: data.pickup,
            lat: typeof data.lat === "string" ? parseFloat(data.lat) : data.lat,
            lng: typeof data.lng === "string" ? parseFloat(data.lng) : data.lng,
            geohash: data.geohash,
          });
        }
      });

      // Sort by distance (nearest first)
      mealsList.sort((a, b) => {
        if (a.distance === undefined && b.distance === undefined) return 0;
        if (a.distance === undefined) return 1;
        if (b.distance === undefined) return -1;
        return a.distance - b.distance;
      });

      // Append or replace meals
      if (loadMore) {
        // Combine with existing meals and re-sort to maintain distance order
        const combinedMeals = [...meals, ...mealsList];
        combinedMeals.sort((a, b) => {
          if (a.distance === undefined && b.distance === undefined) return 0;
          if (a.distance === undefined) return 1;
          if (b.distance === undefined) return -1;
          return a.distance - b.distance;
        });
        setMeals(combinedMeals);
      } else {
        setMeals(mealsList);
      }
    } catch (error) {
      console.error("Error loading meals:", error);
    } finally {
      if (loadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  return (
    <Container>
      <AppHeader
        showNav={true}
        showGreeting={true}
        showOrders={!isArtisan}
        currentPage="feed"
        logoLink="/"
      />

      <Content>
        <PageTitle>
          <FaUtensils style={{ marginRight: "8px" }} /> Discover Amazing Meals
        </PageTitle>
        <Subtitle>
          Order delicious homemade food from local artisans in your area
        </Subtitle>

        <InstantSearch
          searchClient={searchClient}
          indexName="algolia_meals_search_index"
        >
          <Configure
            hitsPerPage={50}
            filters="available:0"
            aroundLatLng={
              userLocation
                ? `${userLocation.lat}, ${userLocation.lng}`
                : undefined
            }
            aroundRadius={
              userLocation ? SEARCH_CONFIG.DEFAULT_RADIUS_METERS : undefined
            }
          />

          <SearchBoxComponent />

          <FiltersBar>
            <FilterButton
              $active={filter === "all"}
              onClick={() => setFilter("all")}
            >
              All Meals
            </FilterButton>
            <FilterButton
              $active={filter === "localOnly"}
              onClick={() => setFilter("localOnly")}
            >
              Local Meals Only
            </FilterButton>
          </FiltersBar>

          {/* Use Algolia search results when user has typed a query */}
          <SearchResultsComponent
            userLocation={userLocation}
            onMealClick={(id) => {
              router.push(`/meals/${id}`);
            }}
            onArtisanClick={(uid) => router.push(`/artisan/${uid}`)}
            isSearching={false}
          />

          {/* Show regular Firestore meals when not searching */}
          <RegularMealsDisplay
            loading={loading}
            meals={meals}
            locationError={locationError}
            filter={filter}
            onMealClick={(id) => {
              router.push(`/meals/${id}`);
            }}
            onArtisanClick={(uid) => router.push(`/artisan/${uid}`)}
            hasMore={hasMore}
            loadingMore={loadingMore}
            observerTarget={observerTarget}
          />
        </InstantSearch>

        {/* Location Gate Overlay - Non-dismissible */}
        {showLocationGate && (
          <LocationGateOverlay>
            <LocationGateModal>
              <LocationGateIcon>
                <FaMapMarkerAlt size={64} color="#C8553D" />
              </LocationGateIcon>
              <LocationGateTitle>Location Required</LocationGateTitle>
              <LocationGateText>
                To view meal details and place orders, we need to know your
                location to show you available delivery options and accurate
                information.
              </LocationGateText>
              <ZipCodeForm onSubmit={handleZipCodeSubmit}>
                <ZipCodeInput
                  type="text"
                  placeholder="Enter zip code (e.g., 90210)"
                  value={zipCode}
                  onChange={(e) => {
                    setZipCode(e.target.value);
                    setZipCodeError("");
                  }}
                  disabled={zipCodeLoading}
                  maxLength={10}
                  autoFocus
                />
                <ZipCodeButton type="submit" disabled={zipCodeLoading}>
                  {zipCodeLoading ? "Loading..." : "Continue"}
                </ZipCodeButton>
              </ZipCodeForm>
              {zipCodeError && <ErrorText>{zipCodeError}</ErrorText>}
            </LocationGateModal>
          </LocationGateOverlay>
        )}
      </Content>
      <Footer />
    </Container>
  );
}

// Component to show regular meals when not searching
interface RegularMealsDisplayProps {
  loading: boolean;
  meals: Meal[];
  locationError: string;
  filter: "all" | "localOnly";
  onMealClick: (id: string) => void;
  onArtisanClick: (uid: string) => void;
  hasMore: boolean;
  loadingMore: boolean;
  observerTarget: React.RefObject<HTMLDivElement>;
}

function RegularMealsDisplay({
  loading,
  meals,
  locationError,
  filter,
  onMealClick,
  onArtisanClick,
  hasMore,
  loadingMore,
  observerTarget,
}: RegularMealsDisplayProps) {
  const { query } = useSearchBox();

  // Hide regular meals if user is searching
  if (query && query.trim() !== "") {
    return null;
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (meals.length > 0) {
    return (
      <>
        <MealsGrid>
          {meals.map((meal) => (
            <MealCard
              key={meal.id}
              meal={meal}
              onClick={() => onMealClick(meal.id)}
              onArtisanClick={() => onArtisanClick(meal.uid)}
            />
          ))}
        </MealsGrid>

        {/* Infinite scroll trigger */}
        {hasMore && (
          <LoadMoreContainer ref={observerTarget}>
            {loadingMore ? (
              <>
                <LoadMoreSpinner />
                <LoadMoreText style={{ marginLeft: "1rem" }}>
                  Loading more meals...
                </LoadMoreText>
              </>
            ) : (
              <LoadMoreText>Scroll for more</LoadMoreText>
            )}
          </LoadMoreContainer>
        )}

        {!hasMore && meals.length > 0 && (
          <LoadMoreContainer>
            <LoadMoreText>No more meals to load</LoadMoreText>
          </LoadMoreContainer>
        )}
      </>
    );
  }

  return (
    <EmptyState>
      <EmptyIcon>
        <FaUtensils size={64} color="#C8553D" />
      </EmptyIcon>
      <EmptyTitle>No meals found</EmptyTitle>
      <EmptyText>
        {filter === "localOnly"
          ? "No meals found within 5 miles of your current location. Try viewing all meals."
          : "No meals found. Check back later for new meals from local artisans!"}
      </EmptyText>
    </EmptyState>
  );
}
